import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatMenu,
  MatMenuItem,
  MatMenuModule,
  MatMenuTrigger,
} from '@angular/material/menu';
import { SiaTranslatePipe } from '@sia/shared';
import {
  differenceInMinutes,
  getHours,
  getMinutes,
  setHours,
  setMinutes,
  startOfDay,
} from 'date-fns';
import { SiaBaseEditorSignalComponent } from '../sia-base-editor/sia-base-editor-signal.component';
import { SiaErrorHintComponent } from '../sia-error-hint/sia-error-hint.component';

@Component({
  selector: 'sia-time-box',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatMenu,
    SiaTranslatePipe,
    MatMenuTrigger,
    MatMenuItem,
    MatMenuModule,
    FormsModule,
    MatIcon,
    SiaErrorHintComponent,
  ],
  templateUrl: './sia-time-box.component.html',
  styleUrl: './sia-time-box.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiaTimeBoxComponent extends SiaBaseEditorSignalComponent {
  // Inputs
  minutesStep = input<number>(15);
  startingTime = input<Date | null | undefined>(undefined);
  newTime = input<{ max: number | undefined; min: number | undefined }>({
    max: 24,
    min: 0,
  });

  // ViewChildren
  menuTrigger = viewChild.required<MatMenuTrigger>('menuTrigger');
  timePickerInput = viewChild.required<ElementRef>('timePickerInput');
  inputElement = viewChild.required<ElementRef>('inputElement');
  scrollableList = viewChild<ElementRef>('scrollableList');

  // Public signals
  stringValue = signal<string>('');
  displayValue = this.stringValue;

  // Private signals
  #lastValidValue = signal<string>('');
  #isMenuOpen = signal<boolean>(false);

  // Computed
  hoursList = computed(() => {
    const step = this.minutesStep();
    const limits = this.newTime();
    const starting = this.startingTime();

    return this.#generateHoursList(step, limits, starting);
  });

  isAppleDevice = computed(() => {
    if (typeof window === 'undefined' || !window.navigator) return false;
    const nav = window.navigator;
    const platform = nav.platform || '';
    const userAgent = nav.userAgent || '';
    const isIOS = /iPhone|iPod/.test(userAgent) || /iPhone|iPod/.test(platform);
    const isIPadOS = platform === 'MacIntel' && nav.maxTouchPoints > 1;
    const isMacOS = platform.startsWith('Mac') && !isIPadOS;
    return isIOS || isIPadOS || isMacOS;
  });

  // Private properties
  #scrollTimeout?: ReturnType<typeof setTimeout>;
  #blurValidationTimeout?: ReturnType<typeof setTimeout>;
  #skipNextBlurValidation = false;
  #lastInputPointerType: string | null = null;
  #userClosedWhileFocused: boolean = false;
  #ignoreNextValueSync = false;

  // Effects
  #valueSyncEffect = effect(
    () => {
      if (this.#ignoreNextValueSync) {
        this.#ignoreNextValueSync = false;
        return;
      }

      const incoming = this.value();

      if (!incoming) {
        untracked(() => {
          if (this.stringValue() !== '') {
            this.stringValue.set('');
          }
        });
        return;
      }

      const coerced = this.#coerceToDate(incoming);

      if (incoming !== coerced) {
        untracked(() => this.value.set(coerced));
        return;
      }

      if (!coerced) return;

      const normalized = new Date(coerced.getTime());
      normalized.setSeconds(0, 0);

      if (!this.#areDatesEqual(coerced, normalized)) {
        untracked(() => this.value.set(normalized));
        return;
      }

      const timeStr = this.#toStringTime(
        getHours(normalized),
        getMinutes(normalized),
      );

      untracked(() => {
        if (this.stringValue() !== timeStr) {
          this.stringValue.set(timeStr);
          this.#lastValidValue.set(timeStr);
        }
      });
    },
    { allowSignalWrites: true },
  );

  #autoScrollEffect = effect(() => {
    const value = this.stringValue();
    const isOpen = this.#isMenuOpen();

    if (isOpen) {
      untracked(() => {
        this.#clearScrollTimeout();
        this.#scrollTimeout = setTimeout(() => {
          this.#scrollToValue(value);
        }, 100);
      });
    }
  });

  ngOnInit() {
    super.ngOnInit();
  }

  override ngOnDestroy() {
    this.#clearScrollTimeout();
    this.#clearBlurValidationTimeout();
    document.documentElement.style.removeProperty('--sia-time-picker-width');
    super.ngOnDestroy();
  }

  #ensureMenuOpen() {
    if (this.isDisabled() || this.getIsMobile() || this.isAppleDevice()) return;

    const input = this.inputElement()?.nativeElement as
      | HTMLInputElement
      | undefined;
    const isFocused = !!input && document.activeElement === input;

    // If you want: "if focused => must be open", remove this guard.
    if (this.#userClosedWhileFocused && isFocused) return;

    if (isFocused && !this.#isMenuOpen()) {
      // Defer helps Safari/iOS focus timing quirks. [page:0]
      setTimeout(() => {
        // Re-check because focus can change before the task runs.
        const stillFocused = document.activeElement === input;
        if (stillFocused && !this.#isMenuOpen()) this.menuTrigger().openMenu();
      }, 0);
    }
  }

  onInputPointerDown(event: PointerEvent) {
    this.#lastInputPointerType = event.pointerType;
  }

  onInputFocus() {
    this.#userClosedWhileFocused = false;

    if ((this.getIsMobile() || this.isAppleDevice()) && this.#isMenuOpen()) {
      this.menuTrigger().closeMenu();
    }

    this.#ensureMenuOpen();
  }

  onIconClick(event: MouseEvent) {
    if (this.isDisabled()) return;

    event.stopPropagation();
    this.#userClosedWhileFocused = false;

    const input = this.inputElement()?.nativeElement as
      | HTMLInputElement
      | undefined;

    if (input && document.activeElement === input) {
      this.#skipNextBlurValidation = true;
      input.blur();
    }

    if (!this.#isMenuOpen()) {
      this.menuTrigger().openMenu();
    }
  }

  onKeyDown(event: KeyboardEvent) {
    const key = event.key;

    if (key === 'Escape') {
      event.preventDefault();
      this.#skipNextBlurValidation = true;
      this.stringValue.set(this.#lastValidValue());
      this.#emitFromStringValue();
      this.#userClosedWhileFocused = true;
      this.menuTrigger().closeMenu();
      (event.target as HTMLInputElement).blur();
      return;
    }

    if (key === 'Enter') {
      event.preventDefault();
      this.#skipNextBlurValidation = true;
      this.#formatAndValidate();
      this.#userClosedWhileFocused = true;
      this.menuTrigger().closeMenu();
      (event.target as HTMLInputElement).blur();
      return;
    }

    if (key === 'Tab') {
      this.#skipNextBlurValidation = true;
      this.#formatAndValidate();
      this.#userClosedWhileFocused = true;
      this.menuTrigger().closeMenu();
      return;
    }

    // Allow special keys
    if (
      [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'Home',
        'End',
        ':',
        '.',
        ',',
      ].includes(key)
    ) {
      return;
    }

    // Allow Ctrl/Cmd shortcuts
    if (
      (event.ctrlKey || event.metaKey) &&
      ['a', 'c', 'v', 'x'].includes(key.toLowerCase())
    ) {
      return;
    }

    // Only allow numbers
    if (!/[0-9]/.test(key)) {
      event.preventDefault();
    }
  }

  onInput(value: string) {
    this.stringValue.set(value);
    this.#emitFromStringValue();
  }

  onBlur(event: FocusEvent) {
    if (this.#skipNextBlurValidation) {
      this.#skipNextBlurValidation = false;
      return;
    }

    this.#clearBlurValidationTimeout();
    this.#blurValidationTimeout = setTimeout(() => {
      const nextFocused =
        (event.relatedTarget as HTMLElement | null) ??
        (document.activeElement as HTMLElement | null);

      if (this.#isElementInsideMenu(nextFocused)) {
        const input = this.inputElement()?.nativeElement as
          | HTMLInputElement
          | undefined;
        input?.focus();
        return;
      }
      this.#userClosedWhileFocused = true;
      this.menuTrigger().closeMenu();
      this.#formatAndValidate();
    }, 0);
  }

  onMenuItemClick(hours: number, minutes: number) {
    this.#setTimeValue(hours, minutes);
    this.#userClosedWhileFocused = true;
    this.menuTrigger().closeMenu();
  }

  onMenuOpen() {
    this.#isMenuOpen.set(true);

    // Set menu width to match input field
    setTimeout(() => {
      const inputEl = this.timePickerInput()?.nativeElement;
      if (inputEl) {
        const inputWidth = inputEl.offsetWidth;
        document.documentElement.style.setProperty(
          '--sia-time-picker-width',
          `${inputWidth}px`,
        );
      }
    }, 0);
  }

  onMenuClose() {
    this.#isMenuOpen.set(false);
    document.documentElement.style.removeProperty('--sia-time-picker-width');

    // If input still focused, re-open.
    this.#ensureMenuOpen();
  }

  handleFormFieldClick(event: MouseEvent) {
    if (this.isAppleDevice() || this.getIsMobile()) {
      const target = event.target as HTMLElement;
      if (!target.closest('button')) {
        event.stopPropagation();
      }
    }
  }

  // Private methods
  #generateHoursList(
    step: number,
    limits: { min: number | undefined; max: number | undefined },
    starting: Date | null | undefined,
  ) {
    const list: {
      hours: number;
      minutes: number;
      description: string;
      label: string;
    }[] = [];

    let minHour = limits?.min ?? 0;
    let maxHour = limits?.max ?? 24;
    const hasValidStarting =
      starting instanceof Date && !isNaN(starting.getTime());

    if (minHour === 0 && maxHour === 0) {
      minHour = 0;
      maxHour = 24;
    }

    // Calculate starting point
    let startMinutes = 0;
    if (hasValidStarting) {
      const startHours = getHours(starting);
      const startMins = getMinutes(starting);
      startMinutes = startHours * 60 + startMins;

      // Round up to next step
      const remainder = startMinutes % step;
      if (remainder !== 0) {
        startMinutes = startMinutes + (step - remainder);
      }
    }

    const totalMinutesInDay = 24 * 60;

    for (
      let minutes = startMinutes;
      minutes < totalMinutesInDay;
      minutes += step
    ) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;

      if (
        hours < minHour ||
        hours > maxHour ||
        (hours === maxHour && mins > 0)
      ) {
        continue;
      }

      const description = this.#toStringTime(hours, mins);

      // Calculate duration from starting time
      let label = description;
      if (hasValidStarting) {
        const eventStart = setMinutes(
          setHours(startOfDay(new Date()), getHours(starting)),
          getMinutes(starting),
        );
        const currentTime = setMinutes(
          setHours(startOfDay(new Date()), hours),
          mins,
        );
        const duration = differenceInMinutes(currentTime, eventStart);

        if (duration >= 0) {
          const h = Math.floor(duration / 60);
          const m = duration % 60;
          const durationStr = h > 0 ? `${h}h ${m}min` : `${m}min`;
          label = `${description} (${durationStr})`;
        } else {
          label = description;
        }
      }

      list.push({
        hours,
        minutes: mins,
        description,
        label,
      });
    }

    return list;
  }

  #formatAndValidate() {
    this.#emitFromStringValue();
  }

  #emitFromStringValue() {
    const parsed = this.#parseTimeString(this.stringValue());
    if (!parsed) {
      this.#ignoreNextValueSync = true;
      this.value.set(undefined);
      this.valueChanged.emit(undefined);
      return;
    }

    const current = this.value();
    const base =
      current instanceof Date ? new Date(current.getTime()) : new Date();
    base.setHours(parsed.hours, parsed.minutes, 0, 0);

    this.#ignoreNextValueSync = true;
    this.value.set(base);
    this.valueChanged.emit(base);
    this.#lastValidValue.set(this.stringValue());
  }

  #parseTimeString(value: string): { hours: number; minutes: number } | null {
    if (!value) return null;

    // Sostituisci separatori comuni con ':'
    const normalized = value.replace(/[.,]/g, ':').trim();

    // Se contiene ':', dividi e valida
    if (normalized.includes(':')) {
      const parts = normalized.split(':');
      // Prendi solo le prime due parti
      const hStr = parts[0]?.trim() || '0';
      const mStr = parts[1]?.trim() || '0';

      const h = parseInt(hStr, 10);
      const m = parseInt(mStr, 10);
      if (!isNaN(h) && !isNaN(m) && h >= 0 && h < 24 && m >= 0 && m < 60) {
        return { hours: h, minutes: m };
      }
      return null;
    }

    // Formati compatti (solo numeri)
    const cleaned = normalized.replace(/[^0-9]/g, '');
    if (cleaned.length === 0 || cleaned.length > 4) return null;

    let h = 0;
    let m = 0;

    switch (cleaned.length) {
      case 1:
      case 2:
        h = parseInt(cleaned, 10);
        m = 0;
        break;
      case 3:
        // 150 -> 01:50 o 15:00?
        // Se la prima cifra è >= 3, allora deve essere HH:M? (es 345 -> ?? no)
        // Standard: 3 cifre = H:MM (es 150 -> 01:50)
        h = parseInt(cleaned.slice(0, 1), 10);
        m = parseInt(cleaned.slice(1), 10);
        break;
      case 4:
        h = parseInt(cleaned.slice(0, 2), 10);
        m = parseInt(cleaned.slice(2), 10);
        break;
    }

    if (h >= 0 && h < 24 && m >= 0 && m < 60) {
      return { hours: h, minutes: m };
    }
    return null;
  }

  #setTimeValue(hours: number, minutes: number) {
    const current = this.value();
    const base =
      current instanceof Date ? new Date(current.getTime()) : new Date();
    base.setHours(hours, minutes, 0, 0);

    this.value.set(base);
    this.valueChanged.emit(base);

    const formatted = this.#toStringTime(hours, minutes);
    this.stringValue.set(formatted);
    this.#lastValidValue.set(formatted);
  }

  #toStringTime(hours: number, minutes: number): string {
    const h = hours.toString().padStart(2, '0');
    const m = minutes.toString().padStart(2, '0');
    return `${h}:${m}`;
  }

  #scrollToValue(value: string) {
    const listElement = this.scrollableList()?.nativeElement;
    if (!listElement) return;

    const parsed = this.#parseTimeString(value);
    if (!parsed) return;

    const targetMinutes = parsed.hours * 60 + parsed.minutes;
    const items = this.hoursList();
    if (items.length === 0) return;

    const exactIndex = items.findIndex(
      (item) => item.hours * 60 + item.minutes === targetMinutes,
    );

    const targetIndex =
      exactIndex >= 0
        ? exactIndex
        : items.findIndex(
            (item) => item.hours * 60 + item.minutes >= targetMinutes,
          );

    const normalizedIndex = targetIndex >= 0 ? targetIndex : items.length - 1;

    const itemHeight = 48; // mat-menu-item height
    listElement.scrollTop = normalizedIndex * itemHeight;
  }

  #coerceToDate(value: any): Date | null {
    if (value === null || value === undefined) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'string') {
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  }

  #areDatesEqual(a: Date | null, b: Date | null): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return a.getTime() === b.getTime();
  }

  #clearScrollTimeout() {
    if (this.#scrollTimeout) {
      clearTimeout(this.#scrollTimeout);
      this.#scrollTimeout = undefined;
    }
  }

  #isElementInsideMenu(element: HTMLElement | null): boolean {
    return !!element?.closest('.mat-mdc-menu-panel');
  }

  #clearBlurValidationTimeout() {
    if (this.#blurValidationTimeout) {
      clearTimeout(this.#blurValidationTimeout);
      this.#blurValidationTimeout = undefined;
    }
  }
}
